<template>
  <div class="borrowerAttributeText">
    <BorrowerAttributeConstructor>
      <template v-slot:input>
        <div class="marketplace-input position-relative form-floating" v-if="getMask(question.alias)">
          <input :id="question.labelId || 'ba' + identifier"
                  :name="question.labelId || 'ba' + identifier"
                  class="form-control"
                  type="text"
                  :inputmode="inputMode"
                  :data-cy="getName()"
                  :class="{
                    'is-invalid': v$.value && v$.value.$error,
                    'disabled': disabled,
                    'disabled-repressed': disabled && repressDisabledStyle
                  }"
                  :placeholder="label"
                  :pattern="question.pattern"
                  :disabled="disabled"
                  v-model="value"
                  :maxlength="maxCharacterLength"
                  v-maska :data-maska="getMask(question.alias)"
                  @blur="onBlur(v$)"
                  @keyup="onChange(v$.value, value)"
                  @change="onChange(v$.value, value)"
          >
          <label class="type-400" :for="question.labelId || 'ba-' + identifier">
            <span>{{ label }}</span>
          </label>
          <span class="invalid-feedback" v-if="v$.value && v$.value.$error">
            <fa-icon :icon="['fal', 'circle-exclamation']"></fa-icon> {{ errorMessage }}
          </span>
          <LendioBootstrapTooltip v-if="tooltipText">
            <span class="text-tooltip-icon position-absolute text-primary" data-bs-toggle="tooltip" data-bs-placement="right" :title="tooltipText">
              <fa-icon :icon="['fal', 'circle-info']"></fa-icon>
            </span>
          </LendioBootstrapTooltip>
        </div>
        <div class="marketplace-input position-relative form-floating" v-else>
          <input :id="question.labelId || 'ba' + identifier"
                  :name="question.labelId || 'ba' + identifier"
                  class="form-control"
                  type="text"
                  :data-cy="getName()"
                  :class="{
                    'is-invalid': v$.value && v$.value.$error,
                    'disabled-repressed': disabled && repressDisabledStyle
                  }"
                  :placeholder="label"
                  :pattern="question.pattern"
                  :disabled="disabled"
                  :maxlength="maxCharacterLength"
                  v-model="value"
                  @blur="onBlur(v$)"
                  @keyup="onChange(v$.value, value)"
                  @change="onChange(v$.value, value)"
          >
          <label class="type-400" :for="question.labelId || 'ba-' + identifier">
            <span>{{ label }}</span>
          </label>
          <span class="invalid-feedback" v-if="v$.value && v$.value.$error">
            <fa-icon :icon="['fal', 'circle-exclamation']"></fa-icon> {{ errorMessage }}
          </span>
          <LendioBootstrapTooltip v-if="tooltipText">
            <span class="text-tooltip-icon position-absolute text-primary" data-bs-toggle="tooltip" data-bs-placement="right" :title="tooltipText">
              <fa-icon :icon="['fal', 'circle-info']"></fa-icon>
            </span>
          </LendioBootstrapTooltip>
        </div>
      </template>
    </BorrowerAttributeConstructor>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, email, requiredIf } from '@vuelidate/validators'
import { taxId } from '~/validators'
import loanPurposeValidator from '~/validators/loanPurposeOther'
import get from 'lodash/get'
import BorrowerAttributeConstructor from './BorrowerAttributeConstructor.vue'
export default {
  components: {
    BorrowerAttributeConstructor,
  },
  props: {
    updateIndex: Number,
    question: Object,
    disabled: Boolean,
    repressDisabledStyle: Boolean,
    updateStoreValue: Function,
  },
  emits: ['invalidity','answer'],
  setup: () => ({ v$: useVuelidate() }),
  data () {
    return {
      value: '',
      errorMessage: 'This field is invalid',
      maxCharacterLength: null
    }
  },
  created () {
    this.$watch('invalid', (invalid) => {
      this.$emit('invalidity', invalid)
    }, {immediate: true})
    this.value = this.question.value
    switch (this.question.alias) {
      case 'user.email':
        this.errorMessage = 'Please enter a valid email'
        break
      case 'borrower.name':
        this.errorMessage = 'Please enter your business name'
        break
      case 'pppOtherLoanPurpose':
        this.errorMessage = 'Please enter your loan purpose. Entry limited to 80 characters.'
        this.maxCharacterLength = 80
        break
      case 'sbaFranchiseId':
        this.errorMessage = 'Franchise code must begin with an S followed by a 4 digit number'
        break
      case 'doingBusinessAsName':
        this.maxCharacterLength = 80
        break
      case 'legal_name':
        this.maxCharacterLength = 80
        break
      case 'owner_street_address':
        this.maxCharacterLength = 80
        break
      case 'owner_street_address_':
        this.maxCharacterLength = 80
        break
      case 'owner_city':
        this.maxCharacterLength = 40
        break
      case 'federalstate_tax_id':
        this.errorMessage = 'Federal/State Tax ID must be 9 digits in length'
        break
      case 'eidlLoanNumber':
        this.errorMessage = 'EIDL Loan Number must be 10 digits in length'
        break
      case 'ppp1SbaNumber':
        this.errorMessage = 'SBA loan numbers must be 10 digits in length'
        break
      case 'widget.peoPhone':
        this.errorMessage = 'Phone number must be 10 digits long'
        break
      default:
        this.errorMessage = 'Please enter a value'
    }
  },
  watch: {
    'question.value' (val, oldVal) {
      if (val !== oldVal && val !== this.value) {
        this.value = val
      }
    }
  },
  computed: {
    identifier() {
      return this.question.id || this.question.alias
    },
    invalid() {
      return this.v$.$invalid
    },
    questionText() {
      return this.question.questionText || ''
    },
    description() {
      if (this.question.alias == 'eidlLoanNumber') {
        return 'Provide the 10-digit loan number associated with your EIDL loan.'
      }
      return this.question.description || ''
    },
    descriptionTooltip() {
      return this.question.descriptionTooltip || ''
    },
    label() {
      return this.question.name || ''
    },
    tooltipText() {
      return this.question.tooltip || this.question.helpText || ''
    },
    helpText() {
      return this.question.tooltip ? this.question.helpText : ''
    },
    errorText() {
      return get(this.v$, 'value.$error') ? this.errorMessage : ''
    },
    disclaimer() {
      return this.question.disclaimer || ''
    },
    inputMode() {
      if (this.question.alias === 'federalstate_tax_id') {
        return 'numeric'
      }
      return 'text'
    }
  },
  validations() {
    if (this.question.alias === 'widget.peoPhone'){
      return {
        value: {
          required,
          minLength: minLength(14)
        }
      }
    }
    if (this.question.alias === 'user.email') {
      return {
        value: {
          email
        }
      }
    }
    if (this.question.alias === 'pppOtherLoanPurpose') {
      return {
        value: {
          loanPurposeValidator,
        }
      }
    }
    // Tax id is ten characters long including dash
    if (this.question.alias === 'federalstate_tax_id') {
      return {
        value: {
          taxId,
          required: requiredIf(() => !this.question.mustAllowNull),
        }
      }
    }
    if (this.question.alias === 'doingBusinessAsName') {
      return {
        value: {required: false}
      }
    }
    return {
      value: {
        minLength: minLength(1),
        required: requiredIf(() => !this.question.mustAllowNull),
      }
    }
  },
  methods: {
    getMask: (alias) => {
      switch (alias) {
        case 'federalstate_tax_id':
        case 'widget.federalstate_tax_id':
          return '##-#######'
        case 'sbaFranchiseId':
          return 'X####'
        case 'widget.peoPhone':
          return '(###) ###-####'
        default:
          return false
      }
    },
    getName: function() {
      return this.identifier === 'borrower.name' ? 'borrowerName' : this.question.alias
    },
    onChange (v$, value) {
      this.$emit('answer', {
        value,
        isInvalid: v$.$invalid
      })
    },
    onBlur (v$) {
      v$.$touch()
    }
  }
}
</script>

<style lang="scss" scoped>
.marketplace-input {
  .text-tooltip-icon {
    top: 1rem;
    right: 1rem;
  }
}
</style>
